import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { ZingChartComponent } from 'app/charts/components/base-classes/zing-chart';
import { ColorThemeService } from 'app/common/services/color-theme.service';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import {
  ISingleContractAnalyzerChartPoint,
  SingleContractAnalyzerType,
  SingleContractAnalyzerTypeEnum
} from 'app/charts-dashboard/interfaces';
import { ThemeColor } from 'app/common/enums/theme-color.enum';
import { locale } from './locale';
import {PaymentWindowService} from '../../../payment-window.service';

@Component({
  selector: 'iv-live-contract-viewer',
  templateUrl: './contract-viewer-chart.component.html',
  styleUrls: ['./contract-viewer-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractViewerChartComponent extends ZingChartComponent implements OnInit, OnChanges {

  constructor(
    protected colorThemeService: ColorThemeService,
    @Inject(AppStore) protected readonly store: Redux.Store<AppState>,
    protected readonly paymentWindowService: PaymentWindowService

  ) {
    super(
      colorThemeService,
      store,
      paymentWindowService
    );
  }

  @Input() public isLoading: boolean;
  @Input() public optionData: ISingleContractAnalyzerChartPoint[];
  @Input() public params: SingleContractAnalyzerType | SingleContractAnalyzerTypeEnum[];

  public noDataAvailable = false;
  public locale = locale;

  private pointsAxisX: string[] = [];
  private pointsAxisY: unknown[] = [];
  private minPercent = 0;
  private maxPercent = 100;
  private scaleYStep = '';
  private isFirst = true;

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.optionData?.currentValue) {
      this.getChartData();
    }
  }

  protected getChartData(): void {
    this.noDataAvailable = false;

    this.pointsAxisX = [];
    this.pointsAxisY = [];

    if (this.optionData?.length > 0) {
      this.optionData.forEach((point: ISingleContractAnalyzerChartPoint) => {
        this.pointsAxisX.push(`${point.time.toLocaleString().substring(0,5)}`);
        this.pointsAxisY.push(point[this.params as SingleContractAnalyzerTypeEnum]);
        this.maxPercent = (Math.ceil(Math.max.apply(Math, this.pointsAxisY) * 10 * 1.05) / 10);
        this.minPercent = (Math.floor(Math.min.apply(Math, this.pointsAxisY) * 10 * 0.95) / 10);
        this.scaleYStep = ((this.maxPercent - this.minPercent) / 10).toFixed(3);
      });
      this.renderChart();
      this.isFirst = false;
    } else {
      this.noDataAvailable = true;
    }
  }

  private produceDotDescription():string {
    if(this.params === SingleContractAnalyzerTypeEnum.IV) return 'IV'
    else if(this.params === SingleContractAnalyzerTypeEnum.LAST_PRICE) return 'Price'
    else if (this.params === SingleContractAnalyzerTypeEnum.BID) return 'Bid'
    else if (this.params === SingleContractAnalyzerTypeEnum.ASK) return 'Ask'
  }

  protected get getChartConfig(): Object {
    return {
      'gui': {
        behaviors: [
          {
            id: 'DownloadPDF',
            enabled: 'none'
          },
          {
            id: 'DownloadSVG',
            enabled: 'none'
          },
          {
            id: 'DownloadCSV',
            enabled: 'none'
          },
          {
            id: 'DownloadXLS',
            enabled: 'none'
          },
          {
            id: 'ViewDataTable',
            enabled: 'none'
          },
          {
            id: 'Print',
            enabled: 'none'
          },
          {
            id: 'ViewSource',
            enabled: 'none'
          },
          {
            id: 'SaveAsImage',
            enabled: 'none'
          },
          {
            id: 'ShowGuide',
            enabled: 'none'
          },
          {
            id: 'HideGuide',
            enabled: 'none'
          },
          {
            id: 'Reload',
            enabled: 'none'
          },
          {
            id: 'About',
            enabled: 'none'
          },
          {
            id: 'CrosshairHide',
            enabled: 'all'
          }
        ]
      },
      'type': 'line',
      'background-color': this.theme === ThemeColor.DARK ? '#242525' : '#fff',
      'utc': true,
      'plotarea': {
        'margin': '15px 30px 45px 45px',
        'background-color': this.backgroundColor
      },
      'scale-x': {
        'values': this.pointsAxisX,
        'shadow': 0,
        'line-color': this.lineColor,
        'tick': {
          'line-color': this.lineColor
        },
        'guide': {
          'line-color': this.lineColor
        },
        'item': {
          'font-color': this.lineColor
        },
        'label': {
          'font-color': this.lineColor,
          'font-weight': 'normal'
        },
        'minor-ticks': 0
      },
      'scale-y': {
        'values': this.minPercent + ':' + this.maxPercent + ':' + this.scaleYStep,
        'format': '%v',
        'line-color': this.lineColor,
        'shadow': 0,
        'tick': {
          'line-color': this.lineColor
        },
        'guide': {
          'line-color': this.lineColor,
          'line-style': 'dashed'
        },
        'item': {
          'font-color': this.lineColor
        },
        'label': {
          'font-color': this.lineColor,
          'font-weight': 'normal'
        },
        'minor-ticks': 0,
        'thousands-separator': ','
      },
      'crosshair-x': {
        'line-color': this.lineColor,
        'plot-label': {
          'text': `${this.produceDotDescription()}: %v<br>Time: %kv`,
          'border-radius': '3px',
          'border-width': '1px',
          'border-color': this.lineColor,
          'padding': '10px',
          'font-weight': 'bold'
        },
        'scale-label': {
          'font-color': '#00baf0',
          'background-color': this.backgroundColor,
          'border-radius': '3px'
        }
      },
      'tooltip': {
        'visible': false
      },
      'plot': {
        'tooltip-text': '%t views: %v<br>%k',
        'shadow': 0,
        'line-width': '3px',
        'marker': {
          'type': 'circle',
          'size': 1
        },
        'hover-marker': {
          'type': 'circle',
          'size': 2,
          'border-width': '1px'
        }
      },
      'series': [
        {
          'values': this.pointsAxisY,
          visible: true,
          // 'text': this.produceDotDescription(),
          'line-color': '#5F5BFF',
          'marker': {
            'background-color': '#5F5BFF',
            'border-width': 1,
            'shadow': 0,
            'border-color': '#6b68f2'
          }
        }
      ]
    };
  }
}
